export interface PresignRequest {
	org: string;
	slug: string;
	fieldId: string;
	fileName: string;
	contentType: string;
	size: number;
}

export interface PresignResponse {
	url: string;
	fields?: Record<string, string>;
	headers?: Record<string, string>;
}

export interface UploadResult {
	success: boolean;
	fileId?: string;
	url?: string;
	error?: string;
}

export type ProgressFn = (pct: number) => void;

// Perform a multipart/form-data POST or PUT upload to a pre-signed URL (S3 compatible)
export async function uploadPresigned(
	presign: PresignResponse,
	file: File,
	onProgress?: ProgressFn,
): Promise<UploadResult> {
	// Determine upload method based on response structure
	const isPostUpload = presign.fields != null;

	if (isPostUpload) {
		// POST multipart upload with form fields
		return uploadPostPresigned(presign, file, onProgress);
	}

	// PUT upload with headers
	return uploadPutPresigned(presign, file, onProgress);
}

// Handle POST multipart uploads (preferred method)
async function uploadPostPresigned(
	presign: PresignResponse,
	file: File,
	onProgress?: ProgressFn,
): Promise<UploadResult> {
	if (!presign.fields) {
		return { success: false, error: "Missing form fields for POST upload" };
	}

	const form = new FormData();
	// Add all presigned fields first
	for (const [k, v] of Object.entries(presign.fields)) {
		form.append(k, v);
	}
	// Add the file
	form.append("file", file);

	// Use XHR to track progress reliably
	const xhr = new XMLHttpRequest();
	const promise = new Promise<UploadResult>((resolve) => {
		xhr.upload.onprogress = (e) => {
			if (onProgress && e.lengthComputable) {
				onProgress(Math.round((e.loaded / e.total) * 100));
			}
		};
		xhr.onerror = () =>
			resolve({ success: false, error: "Network error during upload" });
		xhr.ontimeout = () => resolve({ success: false, error: "Upload timeout" });
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				// S3 commonly returns 204 No Content; the key is echoed in fields/Location headers
				const url = xhr.responseURL || presign.url;
				resolve({ success: true, url });
			} else {
				let errorMsg = `HTTP ${xhr.status}`;
				try {
					const responseText = xhr.responseText;
					if (responseText) {
						const errorData = JSON.parse(responseText);
						errorMsg = errorData.message || errorData.error || errorMsg;
					}
				} catch {
					// Ignore JSON parse errors
				}
				resolve({ success: false, error: errorMsg });
			}
		};
	});

	xhr.open("POST", presign.url);
	xhr.timeout = 5 * 60 * 1000; // 5 minutes timeout

	// Set additional headers if provided
	if (presign.headers) {
		for (const [k, v] of Object.entries(presign.headers)) {
			xhr.setRequestHeader(k, v);
		}
	}

	xhr.send(form);
	return promise;
}

// Handle PUT uploads (fallback method)
async function uploadPutPresigned(
	presign: PresignResponse,
	file: File,
	onProgress?: ProgressFn,
): Promise<UploadResult> {
	// Use XHR to track progress reliably
	const xhr = new XMLHttpRequest();
	const promise = new Promise<UploadResult>((resolve) => {
		xhr.upload.onprogress = (e) => {
			if (onProgress && e.lengthComputable) {
				onProgress(Math.round((e.loaded / e.total) * 100));
			}
		};
		xhr.onerror = () =>
			resolve({ success: false, error: "Network error during upload" });
		xhr.ontimeout = () => resolve({ success: false, error: "Upload timeout" });
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				const url = xhr.responseURL || presign.url;
				resolve({ success: true, url });
			} else {
				let errorMsg = `HTTP ${xhr.status}`;
				try {
					const responseText = xhr.responseText;
					if (responseText) {
						const errorData = JSON.parse(responseText);
						errorMsg = errorData.message || errorData.error || errorMsg;
					}
				} catch {
					// Ignore JSON parse errors
				}
				resolve({ success: false, error: errorMsg });
			}
		};
	});

	xhr.open("PUT", presign.url);
	xhr.timeout = 5 * 60 * 1000; // 5 minutes timeout

	// Set required headers for PUT upload
	if (presign.headers) {
		for (const [k, v] of Object.entries(presign.headers)) {
			xhr.setRequestHeader(k, v);
		}
	} else {
		// Fallback headers for basic PUT upload
		xhr.setRequestHeader(
			"Content-Type",
			file.type || "application/octet-stream",
		);
	}

	xhr.send(file);
	return promise;
}
